import { DumbbellIcon } from "@/assets/images/svgIcons/blocks/dumbbellIcon"
import { Button } from "@/components/ui/baseUi/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { CalendarArrowUpIcon, LightbulbIcon, NotebookPenIcon, PlusIcon, VideoIcon } from "lucide-react"

export function ProgramCalendarSessionFooterContainer({
  className,
  onCreateBlockClick,
  onImportTemplateClick,
  session,
  ...props
}: {
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity
  onCreateBlockClick: (
    session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity,
    newBlockType: SessionBlockType,
  ) => void
  onImportTemplateClick: (session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity) => void
} & React.ComponentProps<"div">) {
  const handleCreateBlockClick = (e: React.MouseEvent<HTMLButtonElement>, newBlockType: SessionBlockType) => {
    e.stopPropagation()

    onCreateBlockClick(session, newBlockType)
  }

  const handleImportTemplateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    onImportTemplateClick(session)
  }

  return (
    <div
      className={cn(
        "group relative mt-2 ml-1 flex w-fit flex-row items-center justify-between overflow-hidden rounded-4xl bg-(--button-outline-bg-primary) ring-1 ring-(--button-outline-border) transition-all duration-300 ease-in-out",
        className,
      )}
      onClick={(e) => {
        e.stopPropagation()
      }}
      {...props}
    >
      <div className="shrink-0 p-2">
        <PlusIcon
          size={16}
          color="currentColor"
          className="text-textDefault-body transition-colors duration-200 group-hover:text-textDisabled"
        />
      </div>

      <div className="z-100 flex w-0 flex-row items-center justify-end overflow-hidden bg-(--button-outline-bg-primary) transition-all duration-300 ease-in-out group-hover:w-44.5">
        <div className="flex w-0 -translate-x-4 flex-row items-center gap-1 opacity-0 transition-[transform] delay-75 duration-300 group-hover:w-44.5 group-hover:translate-x-0 group-hover:opacity-100">
          <ActionButton
            onClick={(e) => {
              handleCreateBlockClick(e, SessionBlockType.SESSION_EXERCISE)
            }}
            tooltip={word("program.session.block.exercise.add")}
          >
            <DumbbellIcon size={16} color="currentColor" />
          </ActionButton>

          <ActionButton
            tooltip={word("program.session.block.media.add")}
            onClick={(e) => {
              handleCreateBlockClick(e, SessionBlockType.MEDIAS)
            }}
          >
            <VideoIcon size={16} color="currentColor" />
          </ActionButton>

          <ActionButton
            onClick={(e) => {
              handleCreateBlockClick(e, SessionBlockType.COACH_NOTE)
            }}
            tooltip={word("program.session.block.note_for_coaches.add")}
          >
            <LightbulbIcon size={16} color="currentColor" />
          </ActionButton>

          <ActionButton
            tooltip={word("program.session.block.note_for_athletes.add")}
            onClick={(e) => {
              handleCreateBlockClick(e, SessionBlockType.ATHLETE_NOTE)
            }}
          >
            <NotebookPenIcon size={16} color="currentColor" />
          </ActionButton>

          <ActionButton
            tooltip={word("program.session.block.template.import")}
            onClick={(e) => {
              handleImportTemplateClick(e)
            }}
          >
            <CalendarArrowUpIcon size={16} color="currentColor" />
          </ActionButton>
        </div>
      </div>
    </div>
  )
}

function ActionButton({
  children,
  tooltip,
  onClick,
  buttonClassName,
  ...buttonProps
}: React.ComponentProps<typeof Button> & {
  children: React.ReactNode
  tooltip: string
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  buttonClassName?: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="iconXs"
            variant="secondary"
            onClick={onClick}
            className={cn("w-0 shrink-0 bg-transparent group-hover:w-8", buttonClassName)}
            {...buttonProps}
          >
            {children}
          </Button>
        }
      />
      <TooltipContent side="bottom" align="center">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}
